import { z } from "zod";
import type { ModelPricing, AgentRecommendation } from "@fathom/core";
import {
  IntelligenceResponse,
  type FetchResult,
  type FetcherOptions,
  type IntelligenceResponse as IntelligenceResponseType,
} from "./types.js";

type Anthropic = import("@anthropic-ai/sdk").default;

/** SEC-05: Mask API keys in error output — show only first 3 and last 3 chars */
export function maskKey(key: string): string {
  if (key.length <= 6) return "***";
  return key.slice(0, 3) + "***" + key.slice(-3);
}

async function getAnthropicClient(apiKey?: string): Promise<Anthropic> {
  const { default: AnthropicSDK } = await import("@anthropic-ai/sdk");
  return new AnthropicSDK({ apiKey: apiKey ?? process.env.ANTHROPIC_API_KEY });
}

const SYSTEM_PROMPT = `You are an AI model pricing analyst. Given the current model pricing and agent recommendations for an AI development workflow tool, review them and suggest updates.

You should:
1. Flag any models with outdated pricing based on your knowledge
2. Suggest new models that should be added (only well-established models from major providers)
3. Flag any recommendation changes (e.g., if a newer model is better suited for a task category)
4. Note any models that may be deprecated or renamed

Respond with ONLY valid JSON matching this schema:
{
  "suggestions": [
    {
      "type": "new-model" | "price-change" | "recommendation-change" | "deprecation" | "general",
      "model": "model-id (optional)",
      "description": "what changed or should change",
      "confidence": "high" | "medium" | "low"
    }
  ],
  "summary": "one sentence overall assessment"
}

If everything looks current, return an empty suggestions array with a summary saying so.`;

export interface IntelligenceFetcherOptions extends FetcherOptions {
  apiKey?: string;
}

export async function fetchIntelligence(
  models: ModelPricing[],
  recommendations: Record<string, AgentRecommendation>,
  options: IntelligenceFetcherOptions = {},
): Promise<FetchResult<IntelligenceResponseType> | null> {
  const now = new Date().toISOString();
  const apiKey = options.apiKey ?? process.env.ANTHROPIC_API_KEY;

  if (options.offline || !apiKey) {
    if (options.verbose) {
      console.log(
        apiKey
          ? "  Intelligence: skipped (offline mode)"
          : "  Intelligence: skipped (no ANTHROPIC_API_KEY)",
      );
    }
    return null;
  }

  try {
    if (options.verbose) {
      console.log("  Intelligence: querying Claude Haiku...");
    }

    const client = await getAnthropicClient(apiKey);

    const modelsDesc = models
      .map((m) => `${m.id} (${m.name}): $${m.input_per_mtok}/$${m.output_per_mtok} per MTok, tier=${m.tier}`)
      .join("\n");

    const recsDesc = Object.entries(recommendations)
      .map(([cat, rec]) => `${cat}: ${rec.model} — ${rec.reason}`)
      .join("\n");

    const userPrompt = `Current model pricing:\n${modelsDesc}\n\nCurrent agent recommendations:\n${recsDesc}`;

    const response = await client.messages.create({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 1024,
      system: SYSTEM_PROMPT,
      messages: [{ role: "user", content: userPrompt }],
    });

    const textBlock = response.content.find((block) => block.type === "text");
    if (!textBlock || textBlock.type !== "text") {
      throw new Error("No text content in response");
    }

    let jsonText = textBlock.text.trim();
    if (jsonText.startsWith("```")) {
      jsonText = jsonText.replace(/^```(?:json)?\n?/, "").replace(/\n?```$/, "");
    }

    const parsed = JSON.parse(jsonText);
    const intelligence = IntelligenceResponse.parse(parsed);

    return {
      data: intelligence,
      source: "api",
      stale: false,
      fetchedAt: now,
    };
  } catch (err) {
    let errorMsg = err instanceof Error ? err.message : String(err);
    // SEC-05: Mask API key in error messages to prevent secret leakage
    if (apiKey) {
      errorMsg = errorMsg.replaceAll(apiKey, maskKey(apiKey));
    }
    if (options.verbose) {
      console.log(`  Intelligence fetch failed: ${errorMsg}`);
    }
    return null;
  }
}
